import { Link } from 'react-router-dom';
import { DemoVideo } from '../components/DemoVideo';

const FEATURES = [
  {
    title: 'Waveform Editor',
    description: 'Slice, tag, and organize samples with precision. Auto-detect BPM, key, and transients. Snap to beat grid.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M2 16h3l2-8 3 16 3-12 3 10 2-6 3 8 2-4 3 6 2-10h3" />
      </svg>
    ),
  },
  {
    title: 'Step Sequencer',
    description: 'Arrange clips on a multi-track grid. Per-track FX chains, automation lanes, A/B pattern comparison.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <rect x="2" y="4" width="6" height="6" rx="1" fill="currentColor" opacity="0.3" />
        <rect x="10" y="4" width="10" height="6" rx="1" fill="currentColor" opacity="0.6" />
        <rect x="6" y="13" width="8" height="6" rx="1" fill="currentColor" opacity="0.3" />
        <rect x="16" y="13" width="6" height="6" rx="1" fill="currentColor" opacity="0.6" />
        <rect x="2" y="22" width="12" height="6" rx="1" fill="currentColor" opacity="0.3" />
        <rect x="22" y="22" width="8" height="6" rx="1" fill="currentColor" opacity="0.6" />
      </svg>
    ),
  },
  {
    title: 'Real-Time Collaboration',
    description: 'See collaborators\u2019 cursors on the waveform. Persistent chat with @mentions. Threaded comments anywhere.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="12" r="4" />
        <circle cx="22" cy="12" r="4" />
        <path d="M2 26c0-4 4-7 8-7 1.5 0 3 .4 4 1" />
        <path d="M30 26c0-4-4-7-8-7-1.5 0-3 .4-4 1" />
        <path d="M16 20v6" />
      </svg>
    ),
  },
  {
    title: 'Smart Analysis',
    description: 'Auto-tag regions as kick, snare, vocal, bass. Pitch detection per sample. Spectrogram view mode.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M4 28V18l4-6 4 4 4-10 4 8 4-2 4 6v10" />
        <line x1="4" y1="28" x2="28" y2="28" />
      </svg>
    ),
  },
  {
    title: 'FX Preview',
    description: 'Audition effects on any region before export. Lowpass, reverb, delay, and more with one-click presets.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="10" cy="20" r="3" />
        <circle cx="22" cy="12" r="3" />
        <line x1="10" y1="8" x2="10" y2="17" />
        <line x1="10" y1="23" x2="10" y2="26" />
        <line x1="22" y1="8" x2="22" y2="9" />
        <line x1="22" y1="15" x2="22" y2="26" />
      </svg>
    ),
  },
  {
    title: 'Privacy First',
    description: 'Audio never touches the server. Files stay on your machine. Only metadata is synced for collaboration.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="8" y="14" width="16" height="14" rx="2" />
        <path d="M12 14V10a4 4 0 018 0v4" />
        <circle cx="16" cy="21" r="2" fill="currentColor" />
      </svg>
    ),
  },
];

const STATS = [
  { value: '14', label: 'Built-in effects' },
  { value: '5', label: 'Color themes' },
  { value: '7', label: 'Auto-tag categories' },
  { value: '0', label: 'Audio on server' },
];

export function LandingPage() {
  return (
    <div className="landing">
      {/* Nav */}
      <nav className="landing-nav">
        <span className="landing-brand">SmpleCut</span>
        <div className="landing-nav-links">
          <Link to="/library" className="landing-nav-text">Library</Link>
          <Link to="/pricing" className="landing-nav-text">Pricing</Link>
          <Link to="/changelog" className="landing-nav-text">What's new</Link>
          <Link to="/login" className="btn btn-sm">Login</Link>
          <Link to="/register" className="btn btn-sm btn-primary">Get Started</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <h1 className="landing-title">
            Slice. Tag. Arrange.
            <br />
            <span className="landing-title-accent">Collaborate.</span>
          </h1>
          <p className="landing-subtitle">
            A collaborative sample editor and sequencer built for music producers.
            Precision tools for slicing, smart analysis for tagging, and real-time
            collaboration — all without your audio ever leaving your machine.
          </p>
          <div className="landing-cta">
            <Link to="/register" className="btn btn-primary btn-lg">Start for Free</Link>
            <Link to="/login" className="btn btn-lg">Login</Link>
          </div>
        </div>

        {/* Demo video (falls back to ASCII art when VITE_DEMO_VIDEO_URL is unset) */}
        <DemoVideo />
      </section>

      {/* Stats */}
      <section className="landing-stats">
        {STATS.map(s => (
          <div key={s.label} className="landing-stat">
            <span className="landing-stat-value">{s.value}</span>
            <span className="landing-stat-label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* Features grid */}
      <section className="landing-features">
        <h2 className="landing-section-title">Everything you need</h2>
        <div className="landing-features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="landing-feature-card">
              <div className="landing-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow section */}
      <section className="landing-workflow">
        <h2 className="landing-section-title">Your workflow, supercharged</h2>
        <div className="landing-workflow-steps">
          <div className="landing-step">
            <span className="landing-step-num">01</span>
            <h3>Load audio</h3>
            <p>Drag and drop any audio file. The <strong>SmpleCut Link</strong> app serves files locally — nothing is uploaded.</p>
          </div>
          <div className="landing-step">
            <span className="landing-step-num">02</span>
            <h3>Slice and tag</h3>
            <p>Auto-detect transients, snap to BPM grid, and let smart analysis classify each region.</p>
          </div>
          <div className="landing-step">
            <span className="landing-step-num">03</span>
            <h3>Arrange</h3>
            <p>Drop samples into the sequencer. Add FX, automate volume, and build patterns with A/B comparison.</p>
          </div>
          <div className="landing-step">
            <span className="landing-step-num">04</span>
            <h3>Collaborate</h3>
            <p>Invite your team. See their cursors, chat in real-time, leave comments on any region.</p>
          </div>
        </div>
      </section>

      {/* Link app download */}
      <section className="landing-download">
        <h2 className="landing-section-title">SmpleCut Link</h2>
        <p className="landing-download-subtitle">
          The desktop companion that serves your audio files locally and syncs with Google Drive.
          Your files never leave your machine.
        </p>
        <div className="landing-download-cards">
          <a href="/download/SmpleCut-Link.dmg" className="landing-download-card">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            <span>macOS</span>
          </a>
          <a href="/download/SmpleCut-Link-Setup.exe" className="landing-download-card">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M3 12V6.75l6-1.32v6.48L3 12zm6.13.51l.01 6.42-6.14-1.13V12.5l6.13.01zM9.89 6.37L17 5.25v6.67l-7.11.05V6.37zM17 12.77l-.01 6.65-7.1-1.17V12.73L17 12.77zM17.89 5.1L24 4v8l-6.11-.02V5.1zM24 12.86l-.01 7.98-6.1-1.01V12.85L24 12.86z"/></svg>
            <span>Windows</span>
          </a>
          <div className="landing-download-card disabled">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-2.2 0-4.2-.9-5.64-2.36L12 12V4c4.42 0 8 3.58 8 8s-3.58 8-8 8z"/></svg>
            <span>Linux</span>
            <span className="landing-download-soon">Soon</span>
          </div>
        </div>
        <p className="landing-download-note">
          Connects to Google Drive, Dropbox, and more. Auto-syncs audio to your local machine.
        </p>

        <details className="landing-install-notes">
          <summary>macOS installation notes (unsigned build)</summary>
          <div className="landing-install-body">
            <p>
              SmpleCut Link isn't yet signed with an Apple Developer ID, so on first launch
              macOS will either say <em>"SmpleCut Link" can't be opened because Apple cannot
              check it for malicious software</em> or — more aggressively on recent macOS
              versions — <em>"SmpleCut Link" is damaged and can't be opened. You should move
              it to the Trash.</em> Neither means the app is actually broken; it's just the
              quarantine flag macOS adds to anything downloaded from the web. Use one of the
              two workflows below.
            </p>

            <h4>Option A — Right-click → Open (simplest)</h4>
            <ol>
              <li>Open the <code>.dmg</code> and drag <strong>SmpleCut Link</strong> into your <code>/Applications</code> folder.</li>
              <li>In Finder, <strong>right-click</strong> (or Control-click) the app and choose <strong>Open</strong>.</li>
              <li>When the warning dialog appears, click the <strong>Open</strong> button. macOS remembers the choice and future launches work normally.</li>
            </ol>
            <p className="landing-install-hint">
              If the dialog only offers <em>Cancel</em> / <em>Move to Trash</em> with no <em>Open</em> button, use Option B.
            </p>

            <h4>Option B — Remove the quarantine flag (Terminal)</h4>
            <ol>
              <li>Install the app into <code>/Applications</code> as usual.</li>
              <li>Open <strong>Terminal</strong> (Spotlight → "Terminal").</li>
              <li>
                Paste and run:
                <pre><code>xattr -cr "/Applications/SmpleCut Link.app"</code></pre>
              </li>
              <li>Launch the app normally from Launchpad or Spotlight.</li>
            </ol>
            <p className="landing-install-hint">
              If the <code>.dmg</code> itself refuses to mount, run the same command on the
              downloaded file: <code>xattr -cr ~/Downloads/SmpleCut-Link-*.dmg</code>.
            </p>

            <h4>Is this safe?</h4>
            <p>
              Yes. <code>xattr -cr</code> only clears the extended attribute macOS uses to
              track downloaded files — it doesn't modify the binary or grant it any new
              permissions. SmpleCut Link is built from open source on GitHub Actions using
              the official <code>tauri-apps/tauri-action</code>, and its only job is to serve
              audio from your disk to the SmpleCut web app running locally. Proper Developer
              ID signing + notarization is on the roadmap and will remove these steps.
            </p>
          </div>
        </details>

        <details className="landing-install-notes">
          <summary>Windows installation notes (unsigned build)</summary>
          <div className="landing-install-body">
            <p>
              SmpleCut Link isn't yet signed with a Microsoft Authenticode certificate, so
              Windows will flag it twice: once in your browser when downloading, and again
              via <strong>Microsoft Defender SmartScreen</strong> when you run the installer.
              Neither means the app is broken — it's the reputation system Windows uses for
              any new or rarely-downloaded executable. The steps below walk through both.
            </p>

            <h4>Step 1 — Keep the download</h4>
            <p>
              Most browsers warn that <em>"SmpleCut-Link-Setup.exe isn't commonly downloaded"</em>
              or <em>"can't be downloaded securely"</em>. To keep the file:
            </p>
            <ul>
              <li><strong>Chrome / Edge:</strong> click the <strong>⋯</strong> (or arrow) next to the warning in the downloads bar and choose <strong>Keep</strong>.</li>
              <li><strong>Firefox:</strong> click the download and choose <strong>Allow download</strong>.</li>
            </ul>

            <h4>Step 2 — Bypass SmartScreen</h4>
            <ol>
              <li>Double-click <code>SmpleCut-Link-Setup.exe</code>.</li>
              <li>A blue dialog appears: <em>"Windows protected your PC — Microsoft Defender SmartScreen prevented an unrecognized app from starting."</em></li>
              <li>Click the small <strong>More info</strong> link at the top of the dialog.</li>
              <li>A <strong>Run anyway</strong> button appears — click it to proceed with the install.</li>
              <li>After the installer finishes, launch SmpleCut Link from the Start menu normally.</li>
            </ol>
            <p className="landing-install-hint">
              If Windows doesn't offer a <em>More info</em> link, right-click the
              <code>.exe</code> → <strong>Properties</strong> → check <strong>Unblock</strong>
              at the bottom → <strong>OK</strong>, then double-click again.
            </p>

            <h4>Is this safe?</h4>
            <p>
              Yes. SmartScreen flags any executable without a code-signing certificate or
              established download reputation — it's a warning, not a detection of anything
              malicious. SmpleCut Link is built from open source on GitHub Actions using the
              official <code>tauri-apps/tauri-action</code>, and its only job is to serve
              audio from your disk to the SmpleCut web app running locally. Authenticode
              signing is on the roadmap and will remove these prompts.
            </p>
          </div>
        </details>
      </section>

      {/* CTA */}
      <section className="landing-bottom-cta">
        <h2>Ready to slice?</h2>
        <p>Free to use. No audio uploaded. Ever.</p>
        <Link to="/register" className="btn btn-primary btn-lg">Create Account</Link>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <span className="landing-brand">SmpleCut</span>
        <span className="landing-footer-text">Built for producers who care about their samples.</span>
        <div className="landing-footer-links">
          <Link to="/library" className="landing-nav-text">Library</Link>
          <Link to="/pricing" className="landing-nav-text">Pricing</Link>
          <Link to="/changelog" className="landing-nav-text">What's new</Link>
          <Link to="/privacy" className="landing-nav-text">Privacy</Link>
          <Link to="/terms" className="landing-nav-text">Terms</Link>
          <Link to="/dmca" className="landing-nav-text">DMCA</Link>
          <Link to="/credits" className="landing-nav-text">Credits</Link>
        </div>
      </footer>
    </div>
  );
}
